import { createContext, useContext, useEffect } from 'react';

const noop = () => {};

/**
 * Default value = the flag-OFF / no-provider stub: every hook call is inert,
 * so pages and the SidePanel can be rendered in isolation (tests) and the
 * flag-off build carries no behavior.
 */
export const DISABLED_ONBOARDING = Object.freeze({
  enabled: false,        // onboardingTour flag: tour + hints + Take the tour
  videosEnabled: false,  // helpVideos flag: How-to videos + Contact support
  tourActive: false,
  reportScreen: noop,
  registerNavControls: () => noop,
  startTour: noop,
  prefetchHelpVideos: noop,
  openHelpVideos: noop,
  helpVideosStatus: 'idle',
  hasHelpVideos: false,
});

export const OnboardingContext = createContext(DISABLED_ONBOARDING);

export function useOnboarding() {
  return useContext(OnboardingContext);
}

/**
 * The one hook a page calls: reports "this screen is ready (with its targets
 * rendered)" and, for the dashboard, how many classes loaded. Unmount reports
 * not-ready. Inert without a provider / with the flag off.
 */
export function useOnboardingScreen(key, { ready = false, classCount = 0 } = {}) {
  const { reportScreen } = useOnboarding();
  const isReady = !!ready;
  const count = Number.isFinite(classCount) ? classCount : 0;
  useEffect(() => {
    reportScreen(key, { ready: isReady, classCount: count });
  }, [reportScreen, key, isReady, count]);
  useEffect(() => () => reportScreen(key, { ready: false, classCount: 0 }), [reportScreen, key]);
}
